import { Alert, Box, Button, Card, Flex, Group, Modal, Select, Stack, Switch, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IMeeting } from "@mctypes/meeting.types";
import { IconDeviceFloppy } from "@tabler/icons-react";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { useAppDispatch } from "../../../hooks";
import { ToastMessagesEnums } from "@mcapps/shared/sharedConstants";
import { getProjectIdFromUrl } from "../../utils";
import {
  usePostProjectByProjectIdMeetingNewSingleMeetingMutation,
  usePutProjectByProjectIdMeetingNewMeetingInSeriesMutation,
} from "../../../../openapi";
import { CreatedMeeting, MeetingSerieOption } from "./meetingApiTypes";

const NewMeetingInSeriesModal: React.FC<{
  opened: boolean;
  onClose: () => void;
  meetingSerie?: MeetingSerieOption;
  meetingSeries?: MeetingSerieOption[];
}> = ({ opened, onClose, meetingSerie, meetingSeries }) => {
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(new Date());
  const [meetingTitle, setMeetingTitle] = useState<string>("");
  const [copyAgendaFromPreviousMeeting, setCopyAgendaFromPreviousMeeting] = useState<boolean>(true);
  const [selectedMeetingSerie, setSelectedMeetingSerie] = useState<MeetingSerieOption | null>(meetingSerie ?? null);

  const meetingSerieSelected = (meetingSerie: MeetingSerieOption) => {
    setSelectedMeetingSerie(meetingSerie);
    setMeetingTitle(meetingSerie.name + " - " + new Date().toISOString().split("T")[0].replace(/-/g, "."));
  };

  const history = useHistory();

  const projectId = getProjectIdFromUrl(history.location.pathname);

  const [createMeetingInstance, { isError: createMeetingError, isLoading: isCreatingMeeting }] =
    usePostProjectByProjectIdMeetingNewSingleMeetingMutation();

  const [copyMeeting, { isError: copyMeetingError, isLoading: isCopyingMeeting }] =
    usePutProjectByProjectIdMeetingNewMeetingInSeriesMutation();

  const dispatch = useAppDispatch();
  const isLoading = isCreatingMeeting || isCopyingMeeting;

  const createNewMeetingInSeries = async (
    title: string,
    dateOfMeeting: Date,
    meetingSerieId: string,
    copyAgendaFromPreviousMeeting: boolean,
  ) => {
    let meeting: CreatedMeeting | undefined;
    if (meetingSerieId == null) {
      meeting = await createMeetingInstance({
        projectId: projectId,
        body: {
          startDate: dateOfMeeting.toISOString(),
          title,
        },
      }).unwrap();
    }

    if (meetingSerieId != null) {
      meeting = await copyMeeting({
        projectId: projectId,
        body: {
          meetingSerieId,
          dateOfMeeting: dateOfMeeting.toISOString(),
          copyAgenda: copyAgendaFromPreviousMeeting,
        },
      }).unwrap();
    }

    if (meeting != null) {
      history.replace(`/project/${projectId}/meetings/${meeting._id}`);

      dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.MEETING_ADDED));
    }

    onClose();
  };

  const isError = createMeetingError || copyMeetingError;

  return (
    <Modal opened={opened} onClose={onClose} title={"Nytt møte"} size="md" padding="md" yOffset={140} radius="md">
      <Card p="sm" bg={"#fafafb"} bd={"1px solid var(--mantine-color-warmgray-2)"}>
        <Stack gap="sm">
          {isError && (
            <Alert color="red" variant="light" title="Feil">
              Kunne ikke opprette møtet. Prøv igjen.
            </Alert>
          )}
          {meetingSerie && (
            <Text size="sm">
              Nytt møte i møteserie <b>{meetingSerie.name}</b>
            </Text>
          )}
          {!selectedMeetingSerie && (
            <TextInput
              autoFocus
              label="Møtetittel"
              placeholder="Tittel for enkeltmøte"
              value={meetingTitle}
              onChange={(e) => setMeetingTitle(e.currentTarget.value)}
            />
          )}
          {!meetingSerie && meetingSeries && (
            <Select
              label="Velg møteserie"
              placeholder="Møteserie"
              data={meetingSeries.filter((m) => m.status === "ACTIVE").map((m) => ({ value: m._id, label: m.name }))}
              value={selectedMeetingSerie?._id || null}
              searchable
              onChange={(value) => {
                if (!value) {
                  setSelectedMeetingSerie(null);
                  setMeetingTitle("");
                } else {
                  const ms = meetingSeries.find((m) => m._id === value);
                  if (ms) meetingSerieSelected(ms);
                }
              }}
            />
          )}
          <Box>
            <Text size="md" fw={500} mb="xs">
              Velg dato for møte
            </Text>
            <DateInput
              size="md"
              value={selectedStartDate}
              onChange={(value) => {
                if (!value) return setSelectedStartDate(null);
                // Mantine DateInput should provide Date; safeguard if string
                setSelectedStartDate(typeof value === "string" ? new Date(value) : (value as Date));
              }}
              clearable
              maw={200}
              valueFormat="YYYY-MM-DD"
              placeholder="Velg dato"
              popoverProps={{ withinPortal: true }}
            />
          </Box>
          <Flex justify="space-between">
            <Group gap="xs" align="start">
              <Switch
                checked={!!selectedMeetingSerie && copyAgendaFromPreviousMeeting}
                onChange={(event) => setCopyAgendaFromPreviousMeeting(event.currentTarget.checked)}
                disabled={!selectedMeetingSerie}
                label={
                  <Text size="sm" mt={-4} style={{ lineHeight: 1.2 }}>
                    Kopier agenda fra siste møte
                  </Text>
                }
                size="md"
              />
            </Group>
            <Group justify="flex-end" mt={"md"}>
              <Button
                leftSection={<IconDeviceFloppy size={16} />}
                color="mc-primary"
                variant="filled"
                disabled={
                  isLoading ||
                  (selectedMeetingSerie && !selectedStartDate) ||
                  (!selectedMeetingSerie && (!selectedStartDate || !meetingTitle))
                }
                onClick={() =>
                  createNewMeetingInSeries(
                    meetingTitle,
                    selectedStartDate!,
                    selectedMeetingSerie?._id,
                    copyAgendaFromPreviousMeeting,
                  )
                }
              >
                Opprett
              </Button>
            </Group>
          </Flex>
        </Stack>
      </Card>
    </Modal>
  );
};

export default NewMeetingInSeriesModal;
